<template>
  <div>
    <TopBar />

    <main class="mx-auto mt-10 md:pr-0">
      <!-- Top Section -->
      <div class="flex flex-wrap items-center lg:flex-row-reverse">
        <div class="mb-10 w-full lg:mb-0">
          <VenuesStaticMap
            v-if="showStaticMap && destination.id"
            :width="$screen.name === 'sm' ? 375 : 1280"
            :height="$screen.name === 'sm' ? 256 : 214"
            :destination="destination"
            :venues="venues"
            @click="showStaticMap = false"
          />

          <div v-if="!showStaticMap" class="relative md:mx-0">
            <ClientOnly>
              <div class="pb-64">
                <VenuesMap
                  v-if="destination.id"
                  :venues="venues"
                  :venue-clicked="venueClicked"
                  :destination="destination"
                  :show="true"
                  :is-sticky-on-scroll="false"
                  :with-search-area="false"
                  hide-close
                  disable-scroll-zoom
                />
              </div>
            </ClientOnly>
          </div>
        </div>
      </div>

      <!-- Listing Section -->
      <div v-if="venue1 && venue2" class="mx-auto flex max-w-screen-xl flex-wrap">
        <div class="mt-20 w-full px-4 text-slate">
          <h1 class="mb-6 flex flex-col items-center justify-center rounded-md border border-lime bg-lime px-2 py-6 text-center font-semibold md:flex-row lg:py-10">
            <a href="javascript:void(0)" class="comparison-title link md:text-right" :class="{ active: venueClicked === venue1 }" @click.prevent="venueClicked = venue1">
              <span class="text-2xl lg:text-3xl xl:text-4xl">{{ venue1.name }}</span>
            </a>
            <span class="mx-6 text-lg text-gray-dark xl:text-xl">vs</span>
            <a href="javascript:void(0)" class="comparison-title link md:text-left" :class="{ active: venueClicked === venue2 }" @click.prevent="venueClicked = venue2">
              <span class="text-2xl lg:text-3xl xl:text-4xl">{{ venue2.name }}</span>
            </a>
          </h1>
          <p class="text-center lg:text-lg xl:text-xl">
            {{ comparison.description }}
          </p>
        </div>
        <div
          v-for="(venue, index2) in venues"
          :key="`venue-box-${index2}`"
          class="mt-20 w-full md:px-4 lg:w-1/2"
        >
          <div class="flex rounded-lg bg-white p-5 text-gray-dark shadow-card md:p-6">
            <ComparisonVenue :venue="venue" />
          </div>
        </div>
      </div>
    </main>

    <MainFooter />

    <ExitIntentModal v-if="venue1 && venue2" v-model="showExitIntent" :venue1="venue1" :venue2="venue2" />
    <div ref="exitModalFake" />
  </div>
</template>

<script>
import ouibounce from 'ouibounce'

import { mapState } from 'pinia'
import { useAsyncData } from 'nuxt/app'
import TopBar from '~/components/layout/TopBar'
import venueTypes from '~/config/venue-types.json'
import VenuesMap from '~/components/venues/VenuesMap'
import ComparisonVenue from '~/components/comparison/ComparisonVenue'
import MainFooter from '~/components/layout/MainFooter'
import ExitIntentModal from '~/components/comparison/ExitIntentModal'
import VenuesStaticMap from '~/components/venues/VenuesStaticMap'
import { useDestinationsStore } from '~/stores/destinations'
import { useComparisonStore } from '~/stores/comparison'
import { showError, useHead, useNuxtApp, useRoute } from '#imports'
import { callWithNuxt } from '#app'
import { useLoadDestination } from '~/composables/useLoadDestination'

export default {
  components: { VenuesStaticMap, ExitIntentModal, TopBar, VenuesMap, ComparisonVenue, MainFooter },

  async setup () {
    const route = useRoute()
    const { params } = route
    const app = useNuxtApp()
    const type = params.type.replace(/s$/, '')
    const comparisonsStore = useComparisonStore()

    const venueType = venueTypes.find(v => v.permalink === type)
    if (! venueType) {
      return showError({ statusCode: 404, statusMessage: 'Venue type not found' })
    }

    await useLoadDestination()

    try {
      await callWithNuxt(app, () => useAsyncData(() => comparisonsStore.loadComparison(params.comparison)))
    } catch (e) {
      if (! e.response || (e.response && e.response.status !== 404)) { app.$sentry.captureException(e) }
    }
    if (! comparisonsStore.comparison.data.id) {
      return showError({ statusCode: 404, statusMessage: 'Comparison was not found' })
    }

    try {
      await callWithNuxt(app, () => useAsyncData(() => comparisonsStore.loadVenues()))
    } catch (e) {
      if (! e.response || (e.response && e.response.status !== 404)) { app.$sentry.captureException(e) }
    }
    if (! comparisonsStore.venue1 || ! comparisonsStore.venue2) {
      return showError({ statusCode: 404, statusMessage: 'One of venues was not found' })
    }

    callWithNuxt(app, () => useHead({
      title: comparisonsStore.venue1 && comparisonsStore.venue2 ? `${comparisonsStore.venue1.name} vs ${comparisonsStore.venue2.name} | Tripexpert` : 'Comparison Tripexpert',
      meta: [
        { hid: 'description', name: 'description', content: comparisonsStore.comparison.description },
        { hid: 'og:description', property: 'og:description', content: comparisonsStore.comparison.description },
      ],
    }))
  },

  data: () => {
    return {
      venueClicked: null,
      datesRange: {
        startDate: null,
        endDate: null,
      },

      numRooms: 1,
      showExitIntent: false,
      showStaticMap: false,
    }
  },

  computed: {
    ...mapState(useDestinationsStore, {
      destination: s => s.destination.data,
    }),

    ...mapState(useComparisonStore, {
      comparison: s => s.comparison.data,
      venues: s => [s.venue1, s.venue2],
      venue1: s => s.venue1,
      venue2: s => s.venue2,
    }),
  },

  mounted () {
    if (this.$config.intentLayoutComparison) {
      ouibounce(this.$refs.exitModalFake, {
        cookieName: 'exitModalComparisonShown',
        cookieExpire: 0.5,
        callback: () => {
          this.showExitIntent = true
        },
      })
    }
  },
}
</script>

<style lang="scss" scoped>
.comparison-title {
  h2 {
    position: relative;
    display: inline-block;
  }

  &.active h2:after {
    content: "";
    position: absolute;
    width: 16px;
    height: 16px;
    right: 10px;
    top: -5px;
    background: url('~/assets/images/blue-check.svg');
  }
}
</style>
